<template>
  <el-dialog 
    :title="dialogTitle" 
    :visible.sync="dialogVisible"
    width="60%"
    @close="handleClose"
  >
    <el-form :model="form" label-width="150px" :rules="rules" ref="form" v-loading="loading.detail">
      <el-form-item label="SQL语句" prop="sql">
        <el-input
          type="textarea"
          v-model="form.sql"
          :autosize="{ minRows: 4 }"
          placeholder="请输入SQL语句（必须返回bool值）"
        ></el-input>
        <div style="margin-top: 10px;">
          <el-button 
            type="primary" 
            size="small" 
            @click="handleSqlCheck"
            :disabled="!form.sql"
            :loading="loading.sqlCheck"
          >检验SQL</el-button>
          <div class="validation-results">
            <div class="result-row">
              <div class="result-label">检验状态：</div>
              <div class="result-content">
                <span :style="{ color: getValidateStatusColor }">
                  {{ validateStatusText }}
                </span>
              </div>
            </div>
            <div v-if="form.sqlValidateStatus !== 'uncheck'" class="result-row">
              <div class="result-label">检验结果：</div>
              <div class="result-content">
                <p>{{ form.sqlValidateResult }}</p>
              </div>
            </div>
            <div v-if="form.sqlValidateStatus !== 'uncheck'" class="result-row">
              <div class="result-label">实际执行SQL：</div>
              <div class="result-content">
                {{ form.executedSql }}
              </div>
            </div>
          </div>
        </div>
      </el-form-item>

      <el-form-item label="监控数据表名称" prop="target_table">
        <el-input 
          v-if="!form.id" 
          v-model="form.target_table" 
          placeholder="请输入监控数据表名称"
          style="max-width: 400px"
        ></el-input>
        <span v-else>{{ form.target_table }}</span>
      </el-form-item>

      <el-form-item label="监控类型" prop="type">
        <el-select v-if="!form.id" v-model="form.type" placeholder="请选择">
          <el-option label="缺失值" value="missing"></el-option>
          <el-option label="数据量" value="count"></el-option>
          <el-option label="新增值" value="add"></el-option>
        </el-select>
        <span v-else>{{ typeText }}</span>
      </el-form-item>

      <el-form-item label="监控触发时间" prop="trigger_hour">
        <el-select v-model="form.trigger_hour" placeholder="请选择">
          <el-option
            v-for="h in 24"
            :key="h-1"
            :label="`${h-1}:00`"
            :value="h-1"
          ></el-option>
        </el-select>
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button 
        type="primary" 
        @click="handleSubmit" 
        :loading="loading.submit"
      >提交</el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'MonitorEditDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    monitorId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      form: {
        id: undefined,
        sql: '',
        target_table: '',
        type: '',
        trigger_hour: '',
        sqlValidated: false,
        sqlValidateStatus: 'uncheck',
        sqlValidateResult: '',
        executedSql: ''
      },
      rules: {
        sql: [
          { required: true, message: '请输入SQL语句' },
          { validator: this.validateSql, trigger: 'custom' }
        ],
        target_table: [
          { required: true, message: '请输入监控数据表名称' }
        ],
        type: [{ required: true, message: '请选择监控类型' }],
        trigger_hour: [{ required: true, message: '请选择触发时间' }]
      },
      loading: {
        detail: false,
        sqlCheck: false,
        submit: false
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    dialogTitle() {
      return this.monitorId ? '编辑监控' : '新建监控'
    },
    validateStatusText() {
      const statusMap = {
        uncheck: '未检验',
        success: '检验通过',
        error: '检验失败'
      }
      return statusMap[this.form.sqlValidateStatus]
    },
    getValidateStatusColor() {
      const colorMap = {
        uncheck: '#909399',
        success: '#67C23A',
        error: '#F56C6C'
      }
      return colorMap[this.form.sqlValidateStatus]
    },
    typeText() {
      const typeMap = {
        missing: '缺失值',
        count: '数据量',
        add: '新增值'
      }
      return typeMap[this.form.type] || this.form.type
    }
  },
  watch: {
    visible(val) {
      if (val && this.monitorId) {
        this.getMonitorDetail(this.monitorId)
      }
      if (val && !this.monitorId) {
        this.resetForm()
      }
    },
    'form.sql': {
      handler: 'handleSqlChange'
    }
  },
  methods: {
    resetForm() {
      this.form = {
        id: undefined,
        sql: '',
        target_table: '',
        type: '',
        trigger_hour: '',
        sqlValidated: false,
        sqlValidateStatus: 'uncheck',
        sqlValidateResult: '',
        executedSql: ''
      }
      if (this.$refs.form) {
        this.$refs.form.resetFields()
      }
    },
    async getMonitorDetail(id) {
      this.loading.detail = true
      try {
        const res = await this.$http.get('/etp/v1.0/monitor/detail?id=' + id)
        if (res.data.err_code === 0) {
          this.form = {
            ...this.form,
            id,
            sql: res.data.data.sql,
            target_table: res.data.data.target_table,
            type: res.data.data.type,
            trigger_hour: res.data.data.trigger_hour
          }
        }
      } catch (error) {
        console.error(error)
        this.$message.error('获取详情失败')
      } finally {
        this.loading.detail = false
      }
    },
    handleClose() {
      this.resetForm()
      this.dialogVisible = false
    },
    async handleSqlCheck() {
      this.loading.sqlCheck = true
      try {
        // 1. 创建SQL检查任务
        const createRes = await this.$http.post('/etp/v1.0/monitor/sql-check/create', {
          sql: this.form.sql
        })
        
        if (createRes.data.err_code !== 0) {
          throw new Error(createRes.data.err_msg || '创建SQL检查任务失败')
        }

        const { task_id, sql } = createRes.data.data
        this.form.executedSql = sql || ''

        // 2. 轮询获取结果
        const startTime = Date.now()
        const timeout = 5 * 60 * 1000 // 5分钟超时
        
        const pollResult = async () => {
          if (Date.now() - startTime > timeout) {
            throw new Error('SQL检查超时')
          }
          console.log('task_id', task_id)
          const queryRes = await this.$http.get(`/etp/v1.0/monitor/sql-check/query?task_id=${task_id}`, {
            noJudgeErrMsg: true
          })
          const { err_code, data, err_msg } = queryRes.data

          if (err_code === 20306) {
            console.log('任务未完成，继续轮询')
            // 任务未完成，继续轮询
            await new Promise(resolve => setTimeout(resolve, 2000)) // 2秒后重试
            return await pollResult()
          } else if (err_code === 0) {
            console.log('检查成功')
            // 检查成功
            this.form.sqlValidated = true
            this.form.sqlValidateStatus = 'success'
            this.form.sqlValidateResult = String(data.result)
            this.$message.success('SQL校验通过')
            this.$refs.form.validateField('sql')
          } else {
            // 其他错误
            console.log('其他错误')
            throw new Error(err_msg || 'SQL检查失败')
          }
        }

        await pollResult()
      } catch (error) {
        console.log('catch error')
        console.error(error)
        this.form.sqlValidated = false
        this.form.sqlValidateStatus = 'error'
        this.form.sqlValidateResult = error.response?.data?.message || error.message || 'SQL检验失败'
        this.form.executedSql = ''
        this.$message.error(this.form.sqlValidateResult)
      } finally {
        this.loading.sqlCheck = false
      }
    },
    validateSql(rule, value, callback) {
      if (!this.form.sqlValidated) {
        callback(new Error('请先进行SQL校验'))
      } else {
        callback()
      }
    },
    async handleSubmit() {
      this.loading.submit = true
      try {
        await this.$refs.form.validateField('sql')
        await this.$refs.form.validate()
        
        const api = this.form.id ? '/etp/v1.0/monitor/update' : '/etp/v1.0/monitor/create'
        const params = {
          sql: this.form.executedSql,
          target_table: this.form.target_table,
          type: this.form.type,
          trigger_hour: this.form.trigger_hour
        }
        if (this.form.id) {
          params.id = Number(this.form.id)
        }
        const res = await this.$http.post(api, params)
        if (res.data.err_code === 0) {
          this.$message.success('保存成功')
          this.$emit('success')
          this.handleClose()
        } else {
          const err_msg = res.data.err_msg || res.data.error || '保存失败'
          this.$message.error(err_msg)
        }
      } catch (error) {
        console.error(error)
        const err_msg = error?.data?.err_msg ||  error?.data?.error || '保存失败'
        this.$message.error(err_msg)
      } finally {
        this.loading.submit = false
      }
    },
    handleSqlChange() {
      this.form.sqlValidated = false
      this.form.sqlValidateStatus = 'uncheck'
      this.form.sqlValidateResult = ''
      this.form.executedSql = ''
    }
  }
}
</script>

<style scoped>
.validation-results {
  margin-top: 10px;
}
.result-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  align-items: start;
  margin-bottom: 8px;
}
.result-label {
  text-align: left;
}
.result-content {
  text-align: left;
}
.result-content p {
  margin: 0;
}
</style> 